import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import enSchool from './locales/en/school.json'
import esSchool from './locales/es/school.json'

const LANG_KEY = 'school_admin_lang'
const MANUAL_KEY = 'school_admin_lang_manual'

export type SchoolAdminLang = 'en' | 'es'

export function normalizeSchoolAdminLang(value?: string | null): SchoolAdminLang {
  const v = String(value || '').trim().toLowerCase()
  if (v === 'es' || v.startsWith('es-') || v === 'es_419' || v === 'es-419') return 'es'
  return 'en'
}

function readStoredLang(): SchoolAdminLang {
  try {
    return normalizeSchoolAdminLang(localStorage.getItem(LANG_KEY))
  } catch {
    return 'en'
  }
}

const saved = readStoredLang()

i18n.use(initReactI18next).init({
  resources: {
    en: { school: enSchool },
    es: { school: esSchool },
  },
  lng: saved,
  fallbackLng: 'en',
  defaultNS: 'school',
  ns: ['school'],
  interpolation: { escapeValue: false },
})

function applyDocumentLang(lng: SchoolAdminLang) {
  try {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lng === 'es' ? 'es' : 'en'
    }
  } catch {
    /* ignore */
  }
}

applyDocumentLang(saved)

/** User clicked EN/ES — remember override until next login. */
export function setSchoolAdminLanguage(lng: SchoolAdminLang, opts?: { manual?: boolean }) {
  const next = normalizeSchoolAdminLang(lng)
  try {
    localStorage.setItem(LANG_KEY, next)
    if (opts?.manual !== false) {
      localStorage.setItem(MANUAL_KEY, '1')
    }
  } catch {
    /* ignore */
  }
  applyDocumentLang(next)
  return i18n.changeLanguage(next)
}

/**
 * Apply the language from the ThinkClass account (sys_users.language_preference).
 * Used on login and token verify. Skipped if the user manually picked EN/ES
 * unless `force` is true (login always forces account default).
 */
export function applyAccountLanguage(language?: string | null, opts?: { force?: boolean }) {
  const next = normalizeSchoolAdminLang(language)
  try {
    const manual = localStorage.getItem(MANUAL_KEY) === '1'
    if (!opts?.force && manual) return i18n.language
    localStorage.setItem(LANG_KEY, next)
    if (opts?.force) localStorage.removeItem(MANUAL_KEY)
  } catch {
    /* ignore */
  }
  applyDocumentLang(next)
  return i18n.changeLanguage(next)
}

export default i18n
